import { Badge, Button, Card, Input, Spinner } from '@fc/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CalendarDays, ListMusic, Plus, Users } from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { PageHeader } from '../../components/page-header'
import {
  createServicePlan,
  listServicePlans,
  type ServicePlanDto,
} from '../../lib/service-planning-api'

const statusVariant: Record<string, 'success' | 'neutral'> = {
  published: 'success',
  draft: 'neutral',
}

function formatPlanDate(date: string): string {
  // plan_date is a 'YYYY-MM-DD' string; render in local-agnostic UTC to avoid TZ shifts.
  const d = new Date(`${date}T00:00:00Z`)
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

function formatLength(seconds: number): string {
  if (!seconds) return '—'
  const m = Math.round(seconds / 60)
  return `${m} min`
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

function NewPlanForm({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [title, setTitle] = useState('')
  const [planDate, setPlanDate] = useState(todayIso())

  const create = useMutation({
    mutationFn: () => createServicePlan({ title: title.trim(), planDate }),
    onSuccess: (plan) => {
      void queryClient.invalidateQueries({ queryKey: ['service-plans'] })
      navigate(`/app/service-planning/${plan.id}`)
    },
  })

  return (
    <Card className="space-y-4 p-5">
      <h2 className="font-display text-lg font-semibold tracking-tight">New service plan</h2>
      <form
        className="grid gap-4 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault()
          if (title.trim()) create.mutate()
        }}
      >
        <label className="space-y-1.5 text-sm sm:col-span-2">
          <span className="text-muted">Title</span>
          <Input
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Sunday Morning Worship"
            required
          />
        </label>
        <label className="space-y-1.5 text-sm">
          <span className="text-muted">Service date</span>
          <Input
            type="date"
            value={planDate}
            onChange={(e) => setPlanDate(e.target.value)}
            required
          />
        </label>
        <div className="flex items-end gap-2">
          <Button type="submit" disabled={create.isPending || !title.trim()}>
            {create.isPending ? <Spinner /> : <Plus />}
            Create plan
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
        {create.isError && (
          <p className="text-sm text-danger sm:col-span-2">
            {create.error instanceof Error ? create.error.message : 'Could not create the plan.'}
          </p>
        )}
      </form>
    </Card>
  )
}

export function ServicePlansList() {
  const [showForm, setShowForm] = useState(false)
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['service-plans'],
    queryFn: () => listServicePlans(),
  })

  return (
    <div className="space-y-6">
      <PageHeader
        title="Service Plans"
        description="Plan worship services — running order, songs, and who is serving."
        actions={
          <>
            <Button asChild variant="secondary">
              <Link to="/app/service-planning/songs">
                <ListMusic />
                Song library
              </Link>
            </Button>
            <Button onClick={() => setShowForm((s) => !s)}>
              <Plus />
              New plan
            </Button>
          </>
        }
      />

      {showForm && <NewPlanForm onClose={() => setShowForm(false)} />}

      {isLoading ? (
        <Card className="flex items-center justify-center gap-2 py-20 text-muted">
          <Spinner /> Loading plans…
        </Card>
      ) : isError ? (
        <Card className="space-y-2 py-20 text-center">
          <p className="text-sm text-danger">Couldn’t load service plans.</p>
          <p className="text-xs text-dim">
            {error instanceof Error ? error.message : 'Is the API running on :3000?'}
          </p>
        </Card>
      ) : !data || data.length === 0 ? (
        <Card className="py-20 text-center text-sm text-muted">
          No service plans yet. Create your first plan to get started.
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {data.map((plan) => (
            <PlanCard key={plan.id} plan={plan} />
          ))}
        </div>
      )}
    </div>
  )
}

function PlanCard({ plan }: { plan: ServicePlanDto }) {
  return (
    <Link to={`/app/service-planning/${plan.id}`} className="group block">
      <Card className="h-full p-5 transition-colors group-hover:bg-card-hover">
        <div className="flex items-start justify-between gap-3">
          <div className="grid size-10 shrink-0 place-items-center rounded-lg bg-card text-secondary">
            <CalendarDays className="size-5" />
          </div>
          <Badge variant={statusVariant[plan.status] ?? 'neutral'} className="capitalize">
            {plan.status}
          </Badge>
        </div>
        <h2 className="mt-4 font-display text-lg font-semibold tracking-tight text-fg">
          {plan.title}
        </h2>
        <p className="mt-1 text-sm text-muted">{formatPlanDate(plan.planDate)}</p>
        {plan.siteName && <p className="text-xs text-dim">{plan.siteName}</p>}
        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-dim">
          <span className="flex items-center gap-1.5">
            <ListMusic className="size-3.5" />
            {plan.itemCount} item{plan.itemCount === 1 ? '' : 's'}
          </span>
          <span className="flex items-center gap-1.5">
            <Users className="size-3.5" />
            {plan.assignmentCount} serving
          </span>
          <span>{formatLength(plan.totalLengthSeconds)}</span>
        </div>
      </Card>
    </Link>
  )
}
